import '../../modules/mode-components';

import * as angular from 'angular';
import { Subscription } from 'rxjs';

import { AnalyticsEvent, AnalyticsService, RunMethodType } from '@mode/shared/util-segment';
import { CalcFieldsService } from '~/services/calc-fields-service';
import { AppStore } from '~/types/main.types';

import { CalcEditorModalState } from '@mode/legacy/types-app-state';
import { ModalService } from '@mode/shared/ui-angularjs';
import { ChartDesignerActionType } from '~/actions/chart-designer/designer.actions';
import { selectedQuerySelector } from '~/reducers/selectors';
import template from './calc-editor-modal.html';

class CalcEditorModalController implements angular.IController {
  static $inject = ['RxStoreService', 'ModalService', 'AnalyticsService', 'CalcFieldsService'];

  // Bindings
  public container = 'body';
  public isExploring = false;
  public isExploration = false;

  // Local UI State
  private isOpen = false;
  private queryToken: string;
  private modalState: CalcEditorModalState;
  private modal: any;

  private windowClass = 'calc-editor-window';

  // Subscriptions
  private state$: Subscription;
  private query$: Subscription;

  // Event handlers
  private boundMouseDownHandler: any;

  constructor(
    private rxStoreService: AppStore,
    private modalService: ModalService,
    private analyticsService: AnalyticsService,
    private calcFieldsService: CalcFieldsService
  ) {}

  $onInit() {
    this.query$ = this.rxStoreService
      .select(selectedQuerySelector)
      .subscribe((query) => (this.queryToken = query?.token));

    this.state$ = this.calcFieldsService.observables.modalState$.subscribe((state) => {
      this.modalState = state;
      if (state.isOpen) {
        this.doOpen();
      } else {
        this.doClose();
      }
    });
  }

  $onDestroy() {
    if (this.boundMouseDownHandler !== undefined) {
      $('body').off('mousedown', undefined, this.boundMouseDownHandler);
      this.boundMouseDownHandler = undefined;
    }
    this.state$?.unsubscribe();
    this.query$?.unsubscribe();
    if (this.modalState?.isOpen) {
      this.doClose();
      this.onClose();
    }
  }

  // Local Actions

  doOpen() {
    if (!this.isOpen) {
      this.isOpen = true;
      this.doLightboxOpen();
      this.analyticsService.run(RunMethodType.Track, AnalyticsEvent.Name.CalcEditorOpen, {
        query_token: this.queryToken,
      });
      // Allows modal to be draggable on page
      this.doDraggableModal();
    }
  }

  doDraggableModal() {
    this.boundMouseDownHandler = this.mouseDownHandler.bind(this);
    $('body').mousedown(this.boundMouseDownHandler);
  }

  mouseDownHandler(mouseDownEvent: JQueryMouseEventObject) {
    if (this.isOpen && mouseDownEvent.target.classList.contains('draggable-content')) {
      // don't select text while dragging window
      mouseDownEvent.preventDefault();

      // Per: https://stackoverflow.com/a/36201892
      const draggableClass = 'is-dragging';
      const header = document.querySelector('.calc-editor-window .modal-header');
      const modalDialog = document.querySelector('.calc-editor-window .modal-content');
      const offset = header.getBoundingClientRect();

      modalDialog.classList.add(draggableClass);

      const mouseMoveListener = (mouseMoveEvent: JQueryEventObject) => {
        mouseMoveEvent.preventDefault();
        const draggableEl = document.querySelector<HTMLElement>(`.${draggableClass}`);
        if (draggableEl) {
          $(`.${draggableClass}`).offset({
            top: mouseMoveEvent.pageY - (mouseDownEvent.pageY - offset.top),
            left: mouseMoveEvent.pageX - (mouseDownEvent.pageX - offset.left),
          });
        }
      };

      const documentBody = $('body');
      const mouseUpListener = () => {
        modalDialog.classList.remove(draggableClass);
        // Clean up the mouse event listeners
        documentBody.off('mousemove', undefined, mouseMoveListener);
        documentBody.off('mouseup', undefined, mouseUpListener);
      };
      documentBody.mousemove(mouseMoveListener);
      documentBody.mouseup(mouseUpListener);
    }
  }

  doClose() {
    if (this.boundMouseDownHandler !== undefined) {
      $('body').off('mousedown', undefined, this.boundMouseDownHandler);
      this.boundMouseDownHandler = undefined;
    }
    if (this.isOpen) {
      this.isOpen = false;
      this.doLightboxClose();
    }
  }

  doLightboxOpen() {
    const readOnly = this.modalState.readOnly;
    const viewMode = this.modalState.viewMode;
    const appendTo = $(this.container ?? 'body');
    const { windowClass, isExploring, isExploration, onClose } = this;

    this.modal = this.modalService.open(
      // Scope props
      {
        readOnly,
        viewMode,
        isExploring,
        isExploration,
        onClose,
      },
      // Modal options
      {
        template,
        appendTo,
        windowClass,
        keyboard: false,
      },
      true
    );

    this.modal.result.catch(this.onClose);
  }

  doLightboxClose() {
    this.modal?.close(true);
  }

  // Bound Events

  onClose = () => {
    this.rxStoreService.dispatch({
      type: ChartDesignerActionType.CloseCalcEditor,
    });
  };
}

const CalcEditorModalComponent: angular.IComponentOptions = {
  bindings: {
    container: '@?',
    isExploring: '<?',
    isExploration: '<?',
  },
  controller: CalcEditorModalController,
  controllerAs: 'ctrl',
};

angular.module('mode.components').component('calcEditorModal', CalcEditorModalComponent);
